import { useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { ApiError } from '../../lib/apiClient';
import styles from './LoginPage.module.css';

type LocationState = { from?: string } | null;

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as LocationState)?.from ?? '/dashboard';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError('Invalid email or password.');
      } else if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Can't reach the server — check the API URL and try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={styles.shell}>
      <div className={styles.center}>
        <div className={styles.brand}>
          <div className={styles.brandMark}>
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10 3h4l1 3-1 2h-4l-1-2z" />
              <path d="M10 8l-2 8 4 7 4-7-2-8" />
            </svg>
          </div>
          <div className={styles.brandStack}>
            <div className={styles.brandCompany}>
              Bring&nbsp;Me
              <br />
              The&nbsp;Pistols
            </div>
            <span className={styles.brandRule} />
            <div className={styles.brandTool}>
              <span className={styles.diamond}>◆</span> Stratos <span className={styles.ver}>v0.1.0</span>
            </div>
          </div>
        </div>

        <div className={styles.card}>
          <div className={styles.head}>
            <div className={styles.eyebrow}>Sign in</div>
            <h1 className={styles.title}>Welcome back, time for strats.</h1>
            <p className={styles.subtitle}>Access your team's playbook and live strategy sessions</p>
          </div>

          <form className={styles.form} onSubmit={handleSubmit} noValidate>
            {error && (
              <div className={styles.error} role="alert">
                {error}
              </div>
            )}

            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor="email">
                Email
              </label>
              <div className={styles.inputWrap}>
                <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="5" width="18" height="14" rx="2" />
                  <path d="m3 7 9 6 9-6" />
                </svg>
                <input
                  id="email"
                  className={styles.input}
                  type="email"
                  placeholder="you@team.gg"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className={styles.field}>
              <div className={styles.fieldLabel}>
                <label htmlFor="password">Password</label>
                {/* TODO: password reset flow — stub only, per prototype */}
                <a className={styles.link} href="#forgot" onClick={(e) => e.preventDefault()}>
                  Forgot?
                </a>
              </div>
              <div className={styles.inputWrap}>
                <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="4" y="10" width="16" height="11" rx="2" />
                  <path d="M8 10V7a4 4 0 1 1 8 0v3" />
                </svg>
                <input
                  id="password"
                  className={styles.input}
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••••"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className={styles.pwToggle}
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  title={showPassword ? 'Hide password' : 'Show password'}
                >
                  <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                </button>
              </div>
            </div>

            <div className={styles.rowBetween}>
              <label className={styles.checkbox}>
                <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
                <span className={styles.box}>
                  <svg viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12l5 5L20 7" />
                  </svg>
                </span>
                Keep me signed in
              </label>
            </div>

            <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={submitting}>
              {submitting ? 'Signing in…' : 'Sign in'}
              {!submitting && (
                <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              )}
            </button>
          </form>
        </div>

        <div className={styles.foot}>
          Your team isn't set up yet?{' '}
          {/* TODO: self-service access request — stub only */}
          <a href="#request" onClick={(e) => e.preventDefault()}>
            Request access
          </a>
        </div>
      </div>

      <div className={styles.metaCorner}>
        <span className={styles.dot} />
        <span>Relay · Online</span>
      </div>
      <div className={styles.metaRight}>Stratos · Dev</div>
    </div>
  );
}
